<template>
  <div class="wrap">
    <header>
      <h1>图片上传（预签名 PUT 直传）说明</h1>
      <div class="sub">小程序端「以流形式上传」到底是不是流式？一次讲清楚二进制直传的全链路</div>
      <div class="meta">
        <span class="chip">来源：「流式上传」相关的几轮讨论</span>
        <span class="chip">对应实现：shijing-mini-program</span>
        <span class="chip">整理时间：2026-09</span>
      </div>
    </header>

    <div class="note blue">
      <p>本文整理自「流式上传」相关的几轮讨论，对应当前 <code>shijing-mini-program</code> 的实际实现。</p>
      <p><b>相关代码：</b></p>
      <ul class="inline-list">
        <li><code>src/api/upload.js</code> —— 取签名、读文件、PUT 上传</li>
        <li><code>src/components/case-uploader/case-uploader.vue</code> —— 选文件、上传中遮罩、成功/失败处理</li>
        <li><code>src/pages/case-detail/case-detail.vue</code> —— 提交时组装 <code>attachment</code></li>
      </ul>
    </div>

    <!-- ========== 一 ========== -->
    <h2><span class="num">1</span>先给结论</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:300px;">问题</th><th>结论</th></tr></thead>
          <tbody>
            <tr>
              <td>小程序里图片是怎么传给预签名地址的？</td>
              <td><code>readFile</code> 读出 <code>ArrayBuffer</code> → <code>uni.request({ method: 'PUT', data })</code>，<b>请求体就是图片的原始字节</b></td>
            </tr>
            <tr>
              <td>这算「流式上传」吗？</td>
              <td><span class="tag t-warn">不算严格意义的流式</span> 是「<b>二进制直传</b>」：整块读进内存后一次性发送</td>
            </tr>
            <tr>
              <td>小程序有真正的流式（边读边发）吗？</td>
              <td>没有原生 API，需用 <code>FileSystemManager.read</code> 分片 + 对象存储 Multipart Upload 自己实现</td>
            </tr>
            <tr>
              <td>那为什么服务端说「以流形式上传」？</td>
              <td>因为服务端收到的是<b>裸二进制 body</b>（不是 multipart 表单），从 HTTP 传输角度看 body 就是字节流</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 二 ========== -->
    <h2><span class="num">2</span>概念澄清：二进制直传 ≠ 流式上传</h2>
    <p class="lead">两者是不同层面的概念：</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:200px;">说法</th><th>指什么</th><th style="width:260px;">小程序能否做到</th></tr></thead>
          <tbody>
            <tr>
              <td class="k">二进制直传</td>
              <td>请求体是文件的<b>原始字节</b>（而不是 multipart 表单封装）</td>
              <td><span class="tag t-fast">✅ 能</span>（本项目采用）</td>
            </tr>
            <tr>
              <td class="k">流式上传</td>
              <td><b>分块</b>生产 / 消费，边读边发，内存只驻留一个 chunk</td>
              <td><span class="tag t-warn">❌ 无现成 API</span>，需自己拼分片</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p class="lead">「流」有两个层面，注意区分：</p>
    <div class="flow">
      <div class="step">
        <div class="q">1. 传输层</div>
        <div class="a">HTTP body 在网络上本来就是按 TCP 字节流一段段传输的 —— <b>任何请求都是这样</b>（包括一个普通 JSON 请求）。所以「body 是流」这句话对二进制直传成立，但它不说明前端是<b>怎么产出</b> body 的。</div>
      </div>
      <div class="step">
        <div class="q">2. 应用层流式（streaming）</div>
        <div class="a">前端<b>不等文件读完</b>就开始发送，边读边发，内存占用可控。这才是「流式上传」通常的含义。</div>
      </div>
    </div>

    <p class="lead">本项目的实现属于第 1 种：</p>
    <pre class="code"><code>const data = await readFileAsArrayBuffer(localPath)  // 先等文件【全部】读进内存
const putRes = await putFile(uploadUrl, data, ...)   // 再把这一整块发出去</code></pre>
    <div class="note">
      <code>await</code> 那一行是关键：<b>发请求之前文件已完整驻留内存</b>，因此服务端收到裸二进制流 ✅，但前端是「整块缓冲 + 一次性发送」❌（非流式）。
    </div>
    <div class="note blue">
      一句话准确表述：小程序端用的是「<b>以文件二进制作为 PUT 请求体直传（预签名直传）</b>」；严格意义的「流式上传」小程序没有原生支持。
    </div>

    <!-- ========== 三 ========== -->
    <h2><span class="num">3</span>常见疑问 Q&amp;A</h2>

    <h3>Q1：<code>uni.getFileSystemManager().readFile</code> 返回的 <code>res.data</code> 是流式数据吗？</h3>
    <p class="lead"><b>不是。</b>它是一次性读进内存的<b>完整内容</b>。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:230px;">是否传 <code>encoding</code></th><th style="width:170px;"><code>res.data</code> 类型</th><th>含义</th></tr></thead>
          <tbody>
            <tr><td>不传（本项目做法）</td><td class="k">ArrayBuffer</td><td>整个文件的二进制内容</td></tr>
            <tr><td>传 <code>encoding: 'utf8'</code> 等</td><td class="k">String</td><td>整个文件的文本内容</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note"><code>readFile</code> 内部是<b>全量读取</b>：调用返回时文件已全部在内存中，不存在「读到一半」的中间态。</div>

    <h3>Q2：那在小程序里，二进制就是流式上传吗？</h3>
    <p class="lead"><b>不等于。</b></p>
    <ul class="list">
      <li><code>readFile</code> → 完整 <code>ArrayBuffer</code>（整块）</li>
      <li><code>uni.request({ data: arrayBuffer })</code> → 把这一整块作为 body 发出（整块）</li>
    </ul>
    <div class="note">两者都是「全量」，只是<b>形态是二进制而非表单</b>。</div>

    <h3>Q3：拿到 <code>getPreSignedUploadUrl</code> 返回的地址后，图片是怎么传过去的？</h3>
    <p class="lead">三步：</p>
    <div class="flow">
      <div class="step"><div class="q">1</div><div class="a"><code>readFile</code> 把本地图片读成 <code>ArrayBuffer</code>；</div></div>
      <div class="step"><div class="q">2</div><div class="a"><code>uni.request</code> 以 <b>PUT</b> 方式把该 <code>ArrayBuffer</code> 作为 <b>body</b> 发出；</div></div>
      <div class="step"><div class="q">3</div><div class="a">带上与文件真实类型一致的 <code>Content-Type</code>，<b>不带任何业务 token</b>（鉴权签名已在 URL 里）。</div></div>
    </div>
    <p class="lead">实际发出的报文（body 为纯二进制）：</p>
    <pre class="code"><code>PUT /9008/miniapp/push-event/20260921/muaul2mjrn8yxd3g.png?X-Amz-Algorithm=...&X-Amz-Signature=... HTTP/1.1
Host: 110.81.252.14:9002
Content-Type: image/png
Content-Length: 786583

&lt;786583 字节的 PNG 原始二进制&gt;</code></pre>
    <div class="note">body 里<b>没有 multipart 边界、没有表单字段</b>，这就是「直传 / 以流形式上传」的实际含义。</div>

    <h3>Q4：为什么不用 <code>uni.uploadFile</code>？</h3>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:230px;">对比项</th><th><code>uni.uploadFile</code></th><th><code>uni.request</code>（本项目采用）</th></tr></thead>
          <tbody>
            <tr><td>HTTP 方法</td><td>只能 <b>POST</b></td><td>支持 <b>PUT</b></td></tr>
            <tr><td>body 形态</td><td><code>multipart/form-data</code>（含 boundary + 表单字段）</td><td><b>裸二进制</b>（ArrayBuffer 原样）</td></tr>
            <tr><td>适合预签名 URL</td><td><span class="tag t-warn">❌</span> 对象存储预签名 PUT 不认 multipart</td><td><span class="tag t-fast">✅</span></td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 四 ========== -->
    <h2><span class="num">4</span>后端接口约定</h2>
    <p class="lead"><b>获取预签名信息</b></p>
    <pre class="code"><code>GET /app-api/storage/pre-signed-upload?fileName=&lt;包含文件夹的文件名&gt;</code></pre>
    <p class="lead">响应：</p>
    <pre class="code"><code>{
  "code": 0,
  "data": {
    "uploadUrl": "http://&lt;host&gt;:9002/9008/miniapp/push-event/20260921/xxx.png?X-Amz-Algorithm=...&amp;X-Amz-Signature=...",
    "filePath": "http://&lt;host&gt;:9002/9008/push-event/20260921/xxx.png"
  }
}</code></pre>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:150px;">字段</th><th>用途</th></tr></thead>
          <tbody>
            <tr><td class="k">uploadUrl</td><td><b>PUT 上传地址</b>（对象存储预签名），仅用于上传</td></tr>
            <tr><td class="k">filePath</td><td><b>文件地址</b>：既用于页面预览，也作为提交给业务接口的图片地址（如处置接口 <code>attachment</code>）</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <p class="lead"><b>PUT 上传结果</b></p>
    <ul class="list">
      <li>成功：<code>HTTP 200</code>（或 204），<b>响应体为空</b></li>
      <li>失败：返回 XML，例如 <code>&lt;Error&gt;&lt;Code&gt;SignatureDoesNotMatch&lt;/Code&gt;...&lt;/Error&gt;</code></li>
    </ul>

    <!-- ========== 五 ========== -->
    <h2><span class="num">5</span>上传流程</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:60px;">步骤</th><th>动作</th><th style="width:300px;">产物</th></tr></thead>
          <tbody>
            <tr><td>1</td><td><code>uni.chooseMedia</code> / <code>chooseImage</code> 选图</td><td>本地 <code>tempFilePath</code></td></tr>
            <tr><td>2</td><td>生成唯一 <code>fileName</code>（含文件夹）</td><td><code>push-event/20260921/k3f9a2x1.jpg</code></td></tr>
            <tr><td>3</td><td>调 <code>pre-signed-upload</code></td><td><code>{ uploadUrl, filePath }</code></td></tr>
            <tr><td>4</td><td><code>readFile</code> 读成 <code>ArrayBuffer</code></td><td>二进制</td></tr>
            <tr><td>5</td><td><code>uni.request</code> <b>PUT</b> 到 <code>uploadUrl</code>，body = 二进制</td><td>HTTP 200</td></tr>
            <tr><td>6</td><td>写回列表：<code>url = filePath</code>（预览）、<code>remotePath = filePath</code>（提交）</td><td>页面展示 + 后续提交</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note blue">
      页面交互：选择后先用<b>本地路径占位展示</b>（盖「上传中」遮罩），上传成功后把 <code>url</code> 替换为 <code>filePath</code>，遮罩消失；失败则移除该项并 toast。上传中的项带 <code>uploading: true</code>，页面提交前会据此拦截。
    </div>

    <!-- ========== 六 ========== -->
    <h2><span class="num">6</span>核心代码（<code>src/api/upload.js</code>）</h2>

    <h3>① 读文件为 ArrayBuffer</h3>
    <pre class="code"><code>function readFileAsArrayBuffer(filePath) {
  return new Promise((resolve, reject) =&gt; {
    // #ifdef MP-WEIXIN
    uni.getFileSystemManager().readFile({
      filePath,
      success: res =&gt; resolve(res.data),   // 图片的完整二进制（ArrayBuffer）
      fail: err =&gt; reject(new Error((err &amp;&amp; err.errMsg) || '读取文件失败'))
    })
    // #endif
    // #ifndef MP-WEIXIN
    reject(new Error('当前端暂不支持预签名直传，请在微信小程序端使用'))
    // #endif
  })
}</code></pre>

    <h3>② PUT 发送二进制</h3>
    <pre class="code"><code>function putFile(url, data, contentType) {
  return new Promise((resolve, reject) =&gt; {
    uni.request({
      url,                              // getPreSignedUploadUrl 返回的 uploadUrl
      method: 'PUT',
      header: { 'Content-Type': contentType || 'application/octet-stream' },
      data,                             // ArrayBuffer 直接作为 body
      success: resolve,
      fail: reject
    })
  })
}</code></pre>
    <p class="lead">要点：</p>
    <ul class="list">
      <li><b><code>data</code> 直接是 <code>ArrayBuffer</code></b>，<code>uni.request</code> 会原样作为 body 发出，不做包装；</li>
      <li><b>不能带 <code>Authorization</code> / <code>tenant-id</code></b>：鉴权签名已在 URL 中，额外头可能破坏签名。</li>
    </ul>

    <h3>③ 串联流程</h3>
    <pre class="code"><code>export async function uploadFile(localPath, type = 'image') {
  const fileName = buildUploadFileName(localPath, type)
  const res = await getPreSignedUploadUrl(fileName)
  const uploadUrl = (res &amp;&amp; res.uploadUrl) || ''
  if (!uploadUrl) throw new Error('获取上传地址失败')

  const ext = getExt(localPath) || (type === 'video' ? 'mp4' : 'jpg')
  const contentType = MIME_MAP[ext] || (type === 'video' ? 'video/mp4' : 'image/jpeg')

  const data = await readFileAsArrayBuffer(localPath)
  const putRes = await putFile(uploadUrl, data, contentType)

  // 对象存储 PUT 成功返回 2xx，响应体为空，只能用状态码判断
  if (!putRes || putRes.statusCode &lt; 200 || putRes.statusCode &gt;= 300) {
    throw new Error(`上传失败(${(putRes &amp;&amp; putRes.statusCode) || 0})`)
  }

  return {
    uploadUrl,                                        // PUT 上传地址（仅上传用）
    previewUrl: (res &amp;&amp; res.filePath) || uploadUrl    // 文件地址：预览与业务提交共用
  }
}</code></pre>

    <h3>④ 页面调用（<code>case-uploader.vue</code>，多选时逐个串行上传）</h3>
    <pre class="code"><code>async handlePicked(picked) {
  const items = picked.map((f, i) =&gt; ({ ...f, uploading: true, localId: `tmp_${stamp}_${i}` }))
  this.$emit('input', this.fileList.concat(items))

  for (const item of items) {
    try {
      const { previewUrl } = await uploadFile(item.url, item.type)
      this.patchFile(item.localId, {
        url: previewUrl,        // 预览地址（filePath）
        type: item.type,
        remotePath: previewUrl, // 提交给业务接口的地址（同 filePath）
        uploading: false
      })
    } catch (e) {
      this.dropFile(item.localId)
      uni.showToast({ title: (e &amp;&amp; e.message) || '上传失败', icon: 'none' })
    }
  }
}</code></pre>

    <!-- ========== 七 ========== -->
    <h2><span class="num">7</span>与 PC 端实现的对应</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:130px;">环节</th><th>PC（<code>ImageUploadDialog.vue</code>）</th><th>小程序（本项目）</th></tr></thead>
          <tbody>
            <tr><td>取签名</td><td><code>fetch(pre-signed-upload?fileName=xxx)</code></td><td><code>get('/app-api/storage/pre-signed-upload', { fileName })</code></td></tr>
            <tr><td>上传</td><td><code>fetch(url, { method: 'PUT', body: file })</code></td><td><code>readFile → ArrayBuffer → uni.request({ method: 'PUT', data })</code></td></tr>
            <tr><td>Content-Type</td><td><code>file.type</code></td><td>按后缀推断（<code>MIME_MAP</code>），小程序端拿不到 MIME</td></tr>
            <tr><td>成功判定</td><td><code>response.ok</code></td><td><code>statusCode</code> 在 200 ~ 299</td></tr>
            <tr><td>预览地址</td><td>域名 + 相对路径拼接</td><td>后端直接返回 <code>filePath</code></td></tr>
            <tr><td>提交地址</td><td>同上</td><td><code>filePath</code>（与预览同值）</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 八 ========== -->
    <h2><span class="num">8</span>踩坑清单</h2>
    <div class="card">
      <ol class="list num-list">
        <li><b><code>Content-Type</code> 必须与文件真实类型一致</b>：若预签名对 content-type 有约束，传错会报 <code>SignatureDoesNotMatch</code>；</li>
        <li><b>不要带业务 token</b>：预签名 URL 已含鉴权签名，额外 header 没有意义（签名只覆盖 host 时虽不影响，但没必要）；</li>
        <li><b>不要解析上传响应体</b>：成功时 body 为空，只能用 HTTP 状态码判断；</li>
        <li><b>地址分工要清楚</b>：<code>uploadUrl</code> 只用于 PUT；<code>filePath</code> 既做预览又做业务提交；</li>
        <li><b><code>fileName</code> 必须唯一</b>：它就是对象存储的 key（含文件夹），同名会互相覆盖，故使用 <code>目录/日期/时间戳随机串.后缀</code>；</li>
        <li><b>大文件内存</b>：<code>readFile</code> 整块读入内存，图片无压力，<b>视频建议控制在 20MB 内</b>；</li>
        <li><b>条件编译</b>：<code>getFileSystemManager</code> 是微信小程序 API，用 <code>#ifdef MP-WEIXIN</code> 包住，其它端给出明确报错；</li>
        <li><b>host 必须与签名时一致</b>（实际踩过的坑）：预签名 URL 的 host 即签名基准，走网关还是直连 MinIO 必须与后端签名时使用的 endpoint 完全一致，否则一律 403。</li>
      </ol>
    </div>

    <!-- ========== 九 ========== -->
    <h2><span class="num">9</span>如果要实现真正的「流式 / 大文件上传」</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:150px;">方案</th><th>做法</th><th style="width:200px;">适用</th></tr></thead>
          <tbody>
            <tr>
              <td class="k">分片上传</td>
              <td>后端提供「初始化分片 → 每个分片一个预签名 URL → 合并」接口；前端利用 <code>readFile({ position, length })</code> 按段读取并逐片 PUT</td>
              <td>大视频（几十 MB 以上）</td>
            </tr>
            <tr>
              <td class="k">后端中转</td>
              <td>前端 <code>uni.uploadFile</code>（POST multipart）传给后端，由后端流式写入对象存储</td>
              <td>想彻底绕开预签名 host 问题</td>
            </tr>
            <tr>
              <td class="k">现状（整块直传）</td>
              <td><code>readFile</code> 整块 + <code>PUT</code></td>
              <td>图片 / 小视频，最简洁</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <p class="lead"><code>readFile</code> 支持分段读取，可用于分片：</p>
    <pre class="code"><code>uni.getFileSystemManager().readFile({
  filePath,
  position: 0,               // 起始字节
  length: 1 * 1024 * 1024,   // 读取长度（1MB）
  success: res =&gt; { /* res.data 为该分片 */ }
})</code></pre>

    <!-- ========== 十 ========== -->
    <h2><span class="num">10</span>相关文件索引</h2>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:330px;">文件</th><th>职责</th></tr></thead>
          <tbody>
            <tr><td class="k">src/api/upload.js</td><td>生成文件名、取签名、读文件、PUT 上传、返回 <code>{ uploadUrl, previewUrl }</code></td></tr>
            <tr><td class="k">src/components/case-uploader/case-uploader.vue</td><td>选文件、上传中遮罩、成功/失败处理、<code>url</code> / <code>remotePath</code> 写入</td></tr>
            <tr><td class="k">src/pages/case-detail/case-detail.vue</td><td>提交时把 <code>remotePath</code> 组装成 <code>attachment</code> 数组</td></tr>
            <tr><td class="k">src/utils/request.js</td><td>业务请求封装（上传不经过它，因为它会带业务 header）</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <footer>
      本文对应当前 <code>shijing-mini-program</code> 的实际实现，用于说明「预签名 PUT 直传」与严格意义「流式上传」的区别。<br>
      接口地址、host 与签名规则以实际部署环境为准。
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ImageStreamingUpload'
}
</script>

<style scoped>
  .wrap {
    /* 撑满内容区（居中限宽时左右留白由 margin 提供，内边距由内容区统一提供） */
    height: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 12px;
    background: var(--surface-1);
    color: #1a1d21;
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 8px;
  }
  .wrap * {
    box-sizing: border-box;
  }
  .wrap code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--surface-2);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
  }
  header {
    margin-bottom: 20px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  }
  h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 8px;
    letter-spacing: -0.4px;
    color: #1a1d21;
  }
  .sub {
    color: #5a6270;
    font-size: 14px;
  }
  .meta {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .chip {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    color: #5a6270;
  }

  h2 {
    font-size: 19px;
    font-weight: 650;
    margin: 40px 0 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: -0.2px;
    color: #1a1d21;
  }
  h2 .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: #eef3fe;
    color: #2f6fed;
    font-size: 13px;
    font-weight: 700;
    flex: none;
  }
  h3 {
    font-size: 15px;
    font-weight: 650;
    margin: 22px 0 6px;
    color: #1a1d21;
  }
  .lead {
    color: #5a6270;
    margin: 0 0 16px;
    font-size: 13.5px;
  }
  .lead:last-child {
    margin-bottom: 0;
  }

  .card {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .table-box {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  thead th {
    background: var(--surface-1);
    text-align: left;
    font-weight: 600;
    color: #5a6270;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    white-space: nowrap;
    font-size: 12.5px;
  }
  tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    vertical-align: top;
    color: #3a414c;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover {
    background: var(--surface-1);
  }
  td.k {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    color: #1a1d21;
  }

  .list {
    margin: 0;
    padding: 14px 20px 14px 34px;
    font-size: 13.5px;
    color: #3a414c;
  }
  .list li {
    margin: 5px 0;
  }
  .num-list li {
    padding-left: 4px;
  }
  .inline-list {
    margin: 6px 0 0;
    padding-left: 20px;
    font-size: 13px;
    color: #1e3d73;
  }
  .inline-list li {
    margin: 2px 0;
  }

  .tag {
    display: inline-block;
    font-size: 11.5px;
    padding: 2px 8px;
    border-radius: 5px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.6;
  }
  .t-fast {
    background: #e9f7ef;
    color: #12a150;
  }
  .t-warn {
    background: #fdecec;
    color: #d93a3a;
  }

  .code {
    background: #1e2229;
    color: #e6e8eb;
    border-radius: 10px;
    padding: 14px 16px;
    margin: 0 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.65;
    white-space: pre;
  }
  .code code {
    background: transparent;
    padding: 0;
    color: inherit;
    font-size: inherit;
  }

  .note {
    background: #fffdf5;
    border: 1px solid #f0e2c0;
    border-left: 3px solid #e0a800;
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    font-size: 13px;
    color: #5c4a15;
    margin-bottom: 16px;
  }
  .note b {
    color: #3d3110;
  }
  .note.blue {
    background: #eef3fe;
    border-color: #c9dbfb;
    border-left-color: #2f6fed;
    color: #1e3d73;
  }
  .note.blue b {
    color: #14305c;
  }
  .note p {
    margin: 0 0 8px;
  }
  .note p:last-child {
    margin-bottom: 0;
  }

  .flow {
    display: flex;
    flex-direction: column;
    margin: 0 0 16px;
  }
  .step {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 16px;
    border: 1px solid rgba(31, 45, 61, 0.06);
    background: var(--surface-1);
  }
  .step:first-child {
    border-radius: 12px 12px 0 0;
  }
  .step:last-child {
    border-radius: 0 0 12px 12px;
  }
  .step:only-child {
    border-radius: 12px;
  }
  .step + .step {
    border-top: none;
  }
  .step .q {
    font-weight: 600;
    min-width: 200px;
    flex: none;
    font-size: 13.5px;
    color: #1a1d21;
  }
  .step .a {
    color: #5a6270;
    font-size: 13px;
  }

  footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid rgba(31, 45, 61, 0.06);
    color: #8b939f;
    font-size: 12.5px;
    line-height: 1.8;
  }

  @media (max-width: 640px) {
    .wrap {
      padding: 16px;
    }
    .step {
      flex-direction: column;
      gap: 3px;
    }
    .step .q {
      min-width: 0;
    }
    h1 {
      font-size: 22px;
    }
    h2 {
      font-size: 17px;
    }
  }
</style>
